import { useEffect, useState } from "react";

/**
 * PWA install.
 *
 * Chrome, Edge and Samsung fire `beforeinstallprompt` once, early, and only to whoever is listening at that moment.
 * So the event is captured at startup (`startInstallCapture`, from main.tsx) and held until the hub renders the
 * Install card. iOS Safari has no install event, so the card shows Add to Home Screen steps there instead.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type InstallPlatform = "android" | "ios" | "desktop";

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

export function detectPlatform(userAgent = navigator.userAgent): InstallPlatform {
  if (/iphone|ipad|ipod/i.test(userAgent)) return "ios";
  if (/android/i.test(userAgent)) return "android";
  return "desktop";
}

export function isStandalone(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const displayStandalone =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches;
  return iosStandalone || displayStandalone;
}

export function startInstallCapture(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Stop the mini-infobar; we show our own button instead.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

/** Test seam: the module holds the captured prompt between renders. */
export function resetInstallState(): void {
  deferredPrompt = null;
  notify();
}

export function useInstallPrompt() {
  const [canPrompt, setCanPrompt] = useState(deferredPrompt !== null);
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    const update = () => setCanPrompt(deferredPrompt !== null);
    listeners.add(update);
    update();
    return () => {
      listeners.delete(update);
    };
  }, []);

  const platform = detectPlatform();

  const install = async (): Promise<boolean> => {
    const prompt = deferredPrompt;
    if (!prompt) return false;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    // The prompt can be shown once; after that the browser needs a new event.
    deferredPrompt = null;
    notify();
    if (outcome !== "accepted") return false;
    setInstalled(true);
    return true;
  };

  return {
    canPrompt,
    needsManualSteps: platform === "ios" && !installed,
    installed,
    install,
  };
}
